import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Avatar,
  IconButton,
  Chip,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  PersonRemove as PersonRemoveIcon,
} from '@mui/icons-material';
import { useCaasTeam } from '../../hooks/useCaasTeam';
import { deleteTeam, removeTeamMember } from '../../../../services/api/endpoints/caas';
import { useAuth } from '../../../../contexts/AuthContext';
import TeamFormModal from './TeamFormModal';
import AddMemberModal from './AddMemberModal';
import UserDetailModal from '../../../users/components/UserDetailModal';

const CaasTeamDetailPage: React.FC = () => {
  const { clientId, teamId } = useParams<{ clientId: string; teamId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { team, members, isLoading, error, refresh } = useCaasTeam(teamId);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [removingMemberId, setRemovingMemberId] = useState<string | null>(null);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  const handleBack = () => {
    navigate(`/caas/clients/${clientId}`);
  };

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleEditSuccess = () => {
    setIsEditModalOpen(false);
    refresh();
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
    setDeleteError(null);
  };

  const handleDeleteConfirm = async () => {
    if (!user?.token || !team) return;

    try {
      setIsDeleting(true);
      setDeleteError(null);
      await deleteTeam(user.token, team.id);
      navigate(`/caas/clients/${clientId}`);
    } catch (err: any) {
      console.error('Error deleting team:', err);
      setDeleteError(err.message || 'Failed to delete team');
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteDialogOpen(false);
    setDeleteError(null);
  };

  const handleAddMember = () => {
    setIsAddMemberModalOpen(true);
  };

  const handleMemberAdded = () => {
    setIsAddMemberModalOpen(false);
    refresh();
  };

  const handleRemoveMember = async (userId: string) => {
    if (!user?.token || !team) return;

    try {
      setRemovingMemberId(userId);
      setRemoveError(null);
      await removeTeamMember(user.token, team.id, userId);
      refresh();
    } catch (err: any) {
      console.error('Error removing team member:', err);
      setRemoveError(err.message || 'Failed to remove team member');
    } finally {
      setRemovingMemberId(null);
    }
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error || !team) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Client
        </Button>
        <Alert severity="error">{error || 'Team not found'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={handleBack} sx={{ mb: 2 }}>
          Back to Client
        </Button>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            {team.name}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={handleEdit}
            >
              Edit
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteClick}
            >
              Delete
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Team Info Card */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Team Information
        </Typography>
        <Box sx={{ display: 'grid', gap: 2 }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Address
            </Typography>
            <Typography variant="body1">{team.address}</Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Building
            </Typography>
            {team.building ? (
              <Typography variant="body1">
                {team.building.name} - {team.building.address}
              </Typography>
            ) : team.building_id ? (
              <Typography variant="body1" color="text.secondary">
                Building ID: {team.building_id}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                Not specified
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Known Email Domains
            </Typography>
            {team.domains && team.domains.length > 0 ? (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {team.domains.map((domain) => (
                  <Chip
                    key={domain}
                    label={domain}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            ) : (
              <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 0.5 }}>
                No domain restrictions
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Members Section */}
      <Paper sx={{ p: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">Members ({members.length})</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddMember}
            size="small"
          >
            Add Member
          </Button>
        </Box>

        {removeError && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setRemoveError(null)}>
            {removeError}
          </Alert>
        )}

        {members.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
            No members yet. Add your first member to get started.
          </Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.map((member) => (
                  <TableRow key={member.id}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar
                          src={member.image_url}
                          alt={member.display_name || member.email}
                          sx={{ width: 32, height: 32 }}
                        >
                          {(member.display_name || member.email).charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography
                          variant="body2"
                          sx={{
                            cursor: 'pointer',
                            '&:hover': {
                              color: 'primary.main',
                              textDecoration: 'underline',
                            },
                          }}
                          onClick={() => setSelectedUserId(member.id)}
                        >
                          {member.display_name || `${member.first_name || ''} ${member.last_name || ''}`.trim() || member.email}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{member.email}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleRemoveMember(member.id)}
                        disabled={removingMemberId === member.id}
                      >
                        {removingMemberId === member.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <PersonRemoveIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Edit Modal */}
      <TeamFormModal
        open={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSuccess={handleEditSuccess}
        team={team}
      />

      {/* Add Member Modal */}
      <AddMemberModal
        open={isAddMemberModalOpen}
        onClose={() => setIsAddMemberModalOpen(false)}
        onSuccess={handleMemberAdded}
        teamId={teamId}
        existingMemberIds={members.map(m => m.id)}
      />

      {/* User Detail Modal */}
      <UserDetailModal
        open={selectedUserId !== null}
        onClose={() => setSelectedUserId(null)}
        userId={selectedUserId}
      />

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Team</DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText>
            Are you sure you want to delete "{team.name}"?
            {members.length > 0 && (
              <Box component="span" sx={{ display: 'block', mt: 2, fontWeight: 'bold' }}>
                Note: This team has {members.length} member{members.length !== 1 ? 's' : ''}.
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CaasTeamDetailPage;
